import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useMutation } from "react-query";
import { fetcher } from "../api/client";
import { useParams, useHistory, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Container,
  Card,
  CardBody,
  Button,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import clsx from "clsx";

import { convertToBase64, getFileSize } from "../utils";
import MainLayout from "../layouts/MainLayout";
import LoadingButton from "../components/LoadingButton";
import Stepper from "../components/Stepper";
import ContractConfigurationContainer from "../components/ContractConfiguration";
import { EditorDocumentPreview } from "../components/DocumentPreview";
import { useTemplateListDispatch, ADD_TEMPLATE } from "../context/templates";
import { useProfile } from "../api/hooks/profile";
import useQueryParams from "../hooks/useQuery";

import { requestTemplate, createTemplate } from "../api/actions/templates";
import { createContract, convertContractTo } from "../api/actions/contracts";

import fileUpload from "../images/pdf/files-icon.svg";
import mailIcon from "../images/pdf/file-send.svg";
import {
  EditorProvider,
  useEditor,
  useEditorActions,
  SET_DOCUMENT,
  useEd,
} from "../context/editor";
import IACover from "../components/IACover";

const steps = [
  {
    name: "documentReady",
  },
  {
    name: "placeSignatures",
  },
  {
    name: "actions.sendContract",
  },
];

function CreatePDFDocumentPage() {
  const { t } = useTranslation("common");
  const history = useHistory();

  const query = useQueryParams();

  const [editorState, editorDispatch] = useEditor();
  const { checkMissingSignatures } = useEditorActions();
  const [files, setFiles] = useState([]);
  const [hasError, setHasError] = useState(false);
  const { profile } = useProfile();
  const hasFiles = !!files.length;
  const initialStep = useMemo(() => {
    if (
      editorState.isPdf &&
      query.get("stepper") === "true" &&
      editorState.isEditable
    )
      return 2;
    // if (editorState.isTemplate) return 2
    // if (editorState.isPdf && (query.get('stepper') === "false" || !query.has('stepper'))) return 2;
    return 2;
  }, [editorState.isPdf, editorState.isEditable, query]); //editorState.isTemplate,
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [isConverted, setIsConverted] = useState(false);

  const { mutate: mutateCreateContract, isLoading: creatingContract } =
    useMutation((body) => fetcher(createContract(body)));
  const { mutate: mutateConvertContractTo } = useMutation((body) =>
    fetcher(convertContractTo(body))
  );

  const onDrop = useCallback((acceptedFiles) => {
    setFiles(
      acceptedFiles.map((file) => {
        const fileSize = getFileSize(file.size);

        return Object.assign(file, {
          fileSize: `${fileSize.amount.toFixed(2)} ${fileSize.type}`,
        });
      })
    );
  }, []);
  const { getRootProps, getInputProps } = useDropzone({
    accept: "application/pdf",
    onDrop,
  });

  const handleResetUploader = () => {
    setFiles([]);
  };

  const nextStep = useCallback(() => {
    setCurrentStep((step) => step + 1);
  }, []);

  const handleContinue = () => {
    const isMissingSignatures = checkMissingSignatures();
    if (isMissingSignatures) return;
    nextStep();
  };

  const handleContractSent = useCallback(() => {
    nextStep();
  }, [nextStep]);

  const handleGoBack = () => {
    if (
      editorState.isPdf &&
      query.get("stepper") === "true" &&
      editorState.isEditable
    ) {
      setCurrentStep(1);
    } else {
      history.goBack();
    }
  };

  const handleUpload = async () => {
    try {
      const parsedFile = await convertToBase64(files[0]);
      if (
        profile?.externalStorageConfig?.saveOn === "trato" &&
        profile?.storage?.remaining <= 0
      ) {
        return toast.error(t("noMoreStorage"), {
          position: toast.POSITION.TOP_RIGHT,
        });
      }

      const res = await mutateCreateContract(parsedFile);
      if (res?.success) {
        setFiles([]);
        setCurrentStep((step) => step + 1);
        editorDispatch({
          type: SET_DOCUMENT,
          document: res?.data,
          setOriginal: true,
        });
        history.replace(`/documents/${res?.data._id}/edit`);
      } else if (res.error) {
        toast.error(t("errorMessages.templateUploaded"));
      }
    } catch (error) {
      setHasError(true);
    }
  };

  useEffect(() => {
    if (initialStep) {
      setCurrentStep(initialStep);
    }
  }, [initialStep]);

  useEffect(() => {
    if (editorState.isTemplate) {
      setCurrentStep(2);
    }
  }, [editorState.isTemplate]);

  useEffect(() => {
    console.log(editorState?.document);
    console.log(editorState?.document?.aiAnalysis);
  }, [editorState]);
  const handleConvertToTemplate = async () => {
    const res = await mutateConvertContractTo({
      contractId: editorState.documentId,
      to: "template",
    });

    if (res.success) {
      setIsConverted(true);
      toast.success(
        t("successMessages.itemCreated", {
          context: "female",
          name: t("template"),
        })
      );
    } else {
      toast.error(t("errorMessages.convertToTemplate"));
    }
  };
  const [activeTab, setActiveTab] = useState("normal");
  return (
    <MainLayout
      className={clsx(
        query.get("stepper") === "true"
          ? "content-height-footer-stepper"
          : "content-height-footer"
      )}
      footer
      showUpgradeBanner={false}
      paddingBottom={0}
    >
      <>
        {query.get("stepper") === "true" && editorState.isEditable && (
          <Stepper steps={steps} currentStep={currentStep} />
        )}
        {editorState.documentId && editorState.status === "loading" && (
          <Container className="d-flex flex-column justify-content-center align-items-center mvh-75 ">
            <Spinner size="lg" />
            <span>{t("actions.loading")}</span>
          </Container>
        )}
        {(!editorState.documentId || editorState.status === "success") && (
          <Container
            fluid={[1, 2].includes(currentStep)}
            className={clsx(
              "d-flex p-0 overflow-hidden",
              ![1, 2].includes(currentStep) && "p-5",
              currentStep !== 3 && "h-100"
            )}
          >
            {currentStep === 0 && (
              <Card className="mx-auto w-50 border-0 shadow-sm mb-5">
                <CardBody className="text-center pt-4 px-5 pb-5">
                  <h2 className="font-weight-bold mb-0">Sube tu PDF</h2>
                  <p className="text-muted">
                    Enviar tu documento en PDF a firma
                  </p>
                  {!hasFiles && (
                    <div className="custom-file-upload" {...getRootProps()}>
                      <div className="upload-photo">
                        <img
                          src={fileUpload}
                          className="img-fluid mb-4"
                          alt="upload file"
                        />
                        <p className="lead mb-0">Arrastra tu documento aquí</p>
                        <small className="d-block text-primary mb-3">
                          o utiliza el siguiente botón
                        </small>
                        <Button color="primary-gradient" className="shadow-sm">
                          Subir Documento
                        </Button>
                        <input
                          type="file"
                          name="photo"
                          id="upload-photo"
                          {...getInputProps()}
                        />
                      </div>
                    </div>
                  )}
                  {files?.map((file) => (
                    <div
                      className="d-flex mb-1 mr-1 align-items-center justify-content rounded p-3 bg-grey"
                      key={file.name}
                    >
                      <FontAwesomeIcon
                        className="color-accent"
                        icon="file"
                        size="2x"
                      />
                      <div className="d-flex flex-column">
                        <span>{file.name}</span>
                        <span className="text-muted">{file.fileSize}</span>
                      </div>
                    </div>
                  ))}
                  {hasFiles && (
                    <div className="mt-2">
                      <Button
                        className="shadow-sm"
                        size="sm"
                        disabled={creatingContract}
                        onClick={handleResetUploader}
                      >
                        {t("actions.changeDocument")}
                      </Button>
                      <LoadingButton
                        loading={creatingContract}
                        loadingText={t("actions.uploading")}
                        color="accent"
                        size="sm"
                        className="shadow-sm ml-2"
                        onClick={handleUpload}
                      >
                        {t("actions.sendContract")}
                      </LoadingButton>
                    </div>
                  )}
                </CardBody>
              </Card>
            )}
            {[1, 2].includes(currentStep) && (
              <Row noGutters className="p-0 w-100 h-100">
                <Col lg={8} className="p-4 h-100 overflow-auto">
                  {activeTab === "normal" && (
                    <EditorDocumentPreview card header={true} width={600} />
                  )}
                  {activeTab === "IA" &&
                    editorState?.document?.aiAnalysis &&
                    editorState?.document?.aiAnalysis !==
                      "no es contrato de arrendamiento de inmueble ni de compraventa" && (
                      <IACover
                        analysis={JSON.parse(editorState?.document?.aiAnalysis)}
                        header={true}
                      />
                    )}
                </Col>
                <Col lg={4} className="h-100">
                  {/* <div className="p-3 text-center border-black border">
                      <h4>
                        { currentStep === 1 && <strong>{t('index')} - </strong>}
                        { currentStep === 2 && <strong>{t('configuration')} - </strong>}
                        {editorState.document.name}
                      </h4>
                    </div> */}
                  <ContractConfigurationContainer
                    className="h-100"
                    tabs={true}
                    currentTab={currentStep}
                    header={false}
                    isEditable={editorState.isEditable}
                    currentJurisdiction={editorState.jurisdiction}
                    currentSignatureType={editorState.signatureType}
                    footer
                    changeWindow={(e) => setActiveTab(e)}
                    onReturn={handleGoBack}
                    onContractSent={handleContractSent}
                    FooterComponent={({
                      saving,
                      onGoBack,
                      isSending,
                      onSubmit,
                      isEditable,
                    }) => (
                      <Row className="justify-content-center">
                        {currentStep === 2 && (
                          <Col sm={6}>
                            <LoadingButton
                              color="default"
                              loading={saving}
                              loadingText={t("actions.saving")}
                              className="align-self-end animate-up w-100"
                              onClick={handleGoBack}
                            >
                              {t("actions.goBack")}{" "}
                              {/*<FontAwesomeIcon icon="eye" className="ml-2" /> */}
                            </LoadingButton>
                          </Col>
                        )}
                        <Col
                          sm={currentStep === 1 ? 12 : 6}
                          className="text-center"
                        >
                          {currentStep === 1 ? (
                            <Button
                              size="lg"
                              color="accent-100"
                              onClick={handleContinue}
                            >
                              {t("actions.continue")}
                            </Button>
                          ) : (
                            <LoadingButton
                              color="accent-100"
                              disabled={!isEditable}
                              loading={isSending}
                              loadingText={t("actions.sending")}
                              className="align-self-end text-light animate-up w-100 mt-xs-4"
                              onClick={onSubmit}
                            >
                              {t("actions.send")}
                            </LoadingButton>
                          )}
                        </Col>
                      </Row>
                    )}
                  />
                </Col>
              </Row>
            )}
            {currentStep === 3 && (
              <Card className="mx-auto w-50 border-0 shadow-sm mb-5">
                <CardBody className="text-center pt-4 px-5 pb-5">
                  <img
                    src={mailIcon}
                    className="img-fluid mb-4 icon-md"
                    alt="mail icon"
                  />
                  <h2 className="font-weight-bold mb-1">
                    {t("contractSent.title", {
                      context: editorState.document.isTestament
                        ? "testament"
                        : "contract",
                    })}
                  </h2>
                  <p className="text-muted mb-4">
                    {t("contractSent.subtitle")}
                  </p>
                  {editorState.isDynamic && (
                    <Button
                      className="shadow-sm mr-2"
                      color="primary-gradient"
                      onClick={handleConvertToTemplate}
                      disabled={isConverted}
                    >
                      {t("convertToTemplate")}
                    </Button>
                  )}
                  <Button
                    className="shadow-sm"
                    color="accent"
                    tag={Link}
                    to="/documents"
                  >
                    {editorState.isDynamic
                      ? t("actions.finalize")
                      : t("myDocuments")}
                  </Button>
                </CardBody>
              </Card>
            )}
          </Container>
        )}
      </>
    </MainLayout>
  );
}

function CreatePDFDocumentPageContainer() {
  const { documentId, templateId } = useParams();

  return (
    <EditorProvider
      documentId={templateId || documentId}
      mode={templateId ? "template" : "contract"}
    >
      <CreatePDFDocumentPage />
    </EditorProvider>
  );
}

export default CreatePDFDocumentPageContainer;
